<template>
  <div>
    <ProjectDetailsExportScheduleModal
      :custom-views="customViews"
      :value.sync="dialogForLimitByTag"
      @export="exportProject" />
    <v-card class="elevation-0">
      <v-list :key="listKey">
        <!-- export to pdf -->
        <v-list-item
          v-if="isVisiblePrint"
          @click="externalPrint">
          <v-list-item-title>
            Quick PDF Export
          </v-list-item-title>
        </v-list-item>

        <!-- groups -->
        <v-list-group
          v-for="(item, i) in filteredExportItems"
          :key="i"
          no-action>
          <template #activator>
            <v-list-item-title
              class="pointer"
              @click="selectItem(item.template, 'templateName')">
              {{ item.name.title }}
            </v-list-item-title>
          </template>
          <ExportToByCustomViewBlock
            v-if="isShowLimitProducts && item.name.value !== CURRENT_VIEW_VALUE"
            :value="customLimitsByView[item.name.value]"
            :show-tooltip="showCustomViewsTooltip"
            @change="
              changeCustomLimit({
                value: $event,
                type: item.name.value
              })
            " />
          <v-list-item
            v-for="(typeItem, typeIndex) in item.types"
            :key="typeIndex"
            :disabled="typeItem.disabled"
            :class="{ 'item-disabled': typeItem.disabled }">
            <template>
              <v-tooltip
                :disabled="!typeItem.showTooltip"
                left>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="d-flex"
                    v-bind="attrs"
                    v-on="on">
                    <v-icon
                      v-if="typeItem.icon"
                      :style="typeItem.margin && 'margin-right: 2px;'"
                      :color="typeItem.disabled ? 'rgb(187,187,187)' : ''">
                      {{ typeItem.icon }}
                    </v-icon>
                    <img
                      v-else
                      :src="typeItem.customIcon"
                      :alt="typeItem.customIcon"
                      class="type-item-custom-icon">
                    <v-list-item-title
                      class="pointer"
                      @click="
                        exportItemClicked({
                          name: typeItem.customPdfType || item.name.value,
                          templateName: item.template,
                          fileFormat: typeItem.name,
                          exportType: typeItem.value,
                        })
                      ">
                      {{ typeItem.label ? typeItem.label : typeItem.name }}
                      <span
                        v-if="typeItem.desc"
                        class="text-body-2">
                        {{ ` - ${typeItem.desc}` }}
                      </span>
                    </v-list-item-title>
                  </div>
                </template>
                <span>{{ typeItem.tooltipText }}</span>
              </v-tooltip>
            </template>
          </v-list-item>
        </v-list-group>
      </v-list>

      <!-- import csv btn -->
      <v-btn
        :disabled="!canUseExportToComputed"
        color="black"
        class="mx-4"
        elevation="0"
        dark
        @click="importFromCsvDialog = true">
        Import from CSV
      </v-btn>
    </v-card>

    <!-- Export Dialog (with Project Metadata) -->
    <v-dialog
      v-model="exportDialog"
      width="500">
      <v-card class="d-flex flex-column export-dialog-card">
        <v-card-title>
          Export Options
          <span
            class="sourcery__icon-wrapper black"
            @click="exportDialog = false">
            <v-icon
              color="white"
              size="15">
              mdi-close
            </v-icon>
          </span>
        </v-card-title>
        <v-card-text
          class="flex-grow-1 overflow-y-auto"
          style="max-height: 70vh;">
          <div>
            <p class="small-p title-in-modal">
              Project Name
            </p>
            <v-text-field
              v-model="exportProjectName"
              single-line
              dense
              autocomplete="off" />
          </div>
          <div>
            <p class="small-p title-in-modal">
              Issuance Date
            </p>
            <v-text-field
              v-model="exportIssuanceDate"
              single-line
              dense
              autocomplete="off" />
          </div>
          <div>
            <p class="small-p title-in-modal">
              Project Number
            </p>
            <v-text-field
              v-model="exportProjectNumber"
              single-line
              dense
              autocomplete="off" />
          </div>
          <div>
            <p class="small-p title-in-modal">
              Author
            </p>
            <v-text-field
              v-model="exportAuthor"
              single-line
              dense
              autocomplete="off" />
          </div>
          <div>
            <p class="small-p title-in-modal">
              Project Phase / Issuance
            </p>
            <v-text-field
              v-model="exportPhase"
              single-line
              dense
              autocomplete="off" />
          </div>
          <div>
            <p class="small-p title-in-modal">
              Location
            </p>
            <location-field
              single-line
              dense
              placeholder=""
              :location-types="[TYPE_GEOCODE]"
              :current-location="renderLocationText"
              @setLocationData="exportLocation = $event"
              @setAddressData="exportAddress = $event" />
          </div>
          <div class="export-notes">
            <v-textarea
              v-model="exportWSNotes"
              label="Workspace Specification Notes"
              outlined
              dense
              rows="4"
              :auto-grow="false"
              class="mt-8" />
            <v-textarea
              v-model="exportProjectNotes"
              label="Project Specification Notes"
              outlined
              scr
              dense
              rows="4"
              :auto-grow="false"
              class="mt-2" />
          </div>
        </v-card-text>
        <v-card-actions class="justify-center d-flex flex-column align-center gap-4">
          <ExportCheckbox
            label="Export in ALL CAPS"
            @change="setAllCaps($event)" />
          <div>
            <v-btn
              color="blue"
              class="white--text elevation-0"
              :disabled="exporting"
              @click="performExportWithMeta">
              Export
            </v-btn>
            <v-btn
              text
              color="blue"
              :disabled="exporting"
              @click="exportDialog = false">
              Cancel
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- import from csv dialog -->
    <v-dialog
      v-model="importFromCsvDialog"
      width="700">
      <v-card>
        <v-card-title>
          Import from CSV

          <span
            class="sourcery__icon-wrapper black"
            @click="importFromCsvDialog = false">
            <v-icon
              color="white"
              size="15">
              mdi-close
            </v-icon>
          </span>
        </v-card-title>
        <v-card-text class="d-flex flex-column justify-center align-center gap-3">
          <FileUpload
            v-model="importFromCsvFile"
            class="d-flex flex-column justify-center align-center gap-3"
            :multiple="false"
            :drop="true"
            :drop-directory="false"
            @input-filter="inputFilter">
            <img
              src="@/assets/icons/dragAndDrop.png"
              alt="dragAndDrop">
            Copy and paste or drag and drop documents here or click to upload
          </FileUpload>
          <div
            v-if="importFromCsvSelectedName"
            class="import-csv-filename-row text-body-2 gap-2"
            :title="importFromCsvSelectedName">
            <v-icon
              size="18"
              class="flex-shrink-0">
              mdi-file-document-outline
            </v-icon>
            <span class="import-csv-filename__text grey--text text--darken-2">{{ importFromCsvSelectedName }}</span>
            <v-btn
              aria-label="Remove file"
              class="flex-shrink-0"
              icon
              small
              @click.stop="clearImportFromCsvFile">
              <v-icon
                size="20">
                mdi-close
              </v-icon>
            </v-btn>
          </div>
          <v-btn
            v-show="importFromCsvFile.length"
            color="blue"
            class="white--text elevation-0"
            @click="importFromCsv">
            Import CSV
          </v-btn>

          <v-divider class="w-100 my-4" />

          <span class="text-body-2 text-center">
            Convert your existing lighting schedule to a CSV with our AI powered CSV creator tool, or download our template.
          </span>
          <div class="d-flex flex-wrap justify-center align-center gap-4">
            <v-btn
              color="black"
              class="elevation-0"
              outlined
              @click="useAiCsvCreator">
              Use AI CSV Creator
            </v-btn>
            <v-btn
              color="black"
              class="elevation-0"
              outlined
              @click="downloadCsvTemplate">
              Download CSV Template
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import {
  mapState, mapGetters, mapMutations, mapActions,
} from 'vuex';

import FileUpload from 'vue-upload-component';
import AppGoogleLocationField from '@/components/App/AppFields/AppGoogleLocationField';
import { AI_CSV_CREATOR } from '@/constants/urls';
import { NOT_PERMITTED } from '@/constants/userPermissions';
import {
  XLSX,
  PDF,
  DOCX,
  CSV,
  CUT_SHEET,
  HLB_SCHEDULE,
  ELECTRO_VALUE,
  HH_ANGUS_EXPORT,
  TEMPLATE_HH_ANGUS_EXPORT,
  TEMPLATE,
  TEMPLATE_CUT_SHEET_HLB,
  TEMPLATE_MBI_LUMINAIRES_NEW,
  MBI_LUMINAIRES_NEW,
  MBI_LUMINAIRES_OLD,
  SPECIFICATION_SHEET,
  ZIP,
  HH_ANGUS_EXPORT_VALUE,
  TEMPLATE_ZIP,
  SPECIFICATION_SHEETS,
  CURRENT_VIEW_VALUE,
  ELECTRO_LIGHT_EXPORT,
  TEMPLATE_ELECTRO_LIGHT_LUMINAIRE_SCHEDULE,
  MCW_VALUE,
  MCW_EXPORT,
  MCW_TEMPLATE,
  DARKLIGHT_VALUE,
  DARKLIGHT_EXPORT,
  DARKLIGHT_TEMPLATE,
  JOURNEY_WORLD,
  ALL_FIELDS_CSV,
} from '@/constants/exportScheduleItems';
import rawConstants from '@/constants/fileExtensions';

import { SORTABLE_ACTIONS_WIDTH } from '@/constants/scheduleViews.js';
import { TYPE_GEOCODE } from '@/constants/maps';

import ExportProject from '@/mixins/ExportProject';
import ToggleViewCondition from '@/mixins/ToggleViewCondition';

import API from '@/services/graphql';

export default {
  name: 'ProjectDetailsExportTo',
  components: {
    ProjectDetailsExportScheduleModal: () => import('./ProjectDetailsExportScheduleModal'),
    ExportToByCustomViewBlock: () => import('./ProjectDetailsExportToByCustomViewBlock'),
    ExportCheckbox: () => import('./ProjectDetailsExportCheckbox'),
    locationField: AppGoogleLocationField,
    FileUpload,
  },
  mixins: [ExportProject, ToggleViewCondition],
  props: {
    isVisiblePrint: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      usersCanUseHangus: [],
      usersCanUseElctrolight: [],
      usersCanUseMCW: [],
      usersCanUseDarkLight: [
        'daryl.ho@expogain.com',
      ],
      usersCanUseCutSheet: [
        'lgtingtests+usertest21@gmail.com',
        'lgtingtests+CI@gmail.com',
        'aks1k15@gmail.com',
      ],
      usersCanUseJourney: [
        'daryl.ho@expogain.com',
      ],
      showExportMenu: false,
      exportingSubType: null,
      ALL_FIELDS_CSV,
      CURRENT_VIEW_VALUE,
      isVisibleTable: false,
      printFormat: 'a0',
      NOT_PERMITTED,

      // Export Dialog
      exportDialog: false,
      exporting: false,
      exportParams: {
      },
      exportProjectName: '',
      exportIssuanceDate: '', // curr date or issuance date if exp from issuance
      exportProjectNumber: '',
      exportAuthor: '', //wsname
      exportPhase: '', // phase or issuance if exp from issuance
      exportAddress: '',
      exportLocation: '',
      exportWSNotes: '',
      exportProjectNotes: '',

      // Import from CSV Dialog
      importFromCsvDialog: false,
      importFromCsvFile: [],

      TYPE_GEOCODE,
    };
  },
  computed: {
    ...mapState(['role', 'userInfo']),
    ...mapState({
      projectSpecNotes: state => state.ProjectDetails.specNotes,
      projectDetails: state => state.ProjectDetails.detailProjectData,
    }),
    ...mapGetters({
      customViews: 'ScheduleViews/customViewsForExportToSchedule',
      canUseExportTo: 'UserRoles/canUseExportTo',
      canUseExportToForCollaboratorRole: 'UserRoles/canUseExportToForCollaboratorRole',
      activeWorkspaceName: 'Workspace/getActiveWorkspaceName',
      workspaceSpecNotes: 'Workspace/getActiveWorkspaceSpecNotes',
    }),
    ...mapGetters('ScheduleRows', ['sortedViewInfo']),
    ...mapGetters('ProjectDetails', ['getDetailProjectTitle']),
    ...mapGetters('ProjectVersions', ['isVersionMode', 'versionTitle', 'getCurrentVersionTimestamp']),
    ...mapGetters('ScheduleViews', ['tableHeaders', 'selectedView']),
    /**
     * Compute whether the user can use the export feature based on their role.
     *
     * @returns {boolean} `true` if the user can use the export feature, otherwise `false`.
     */
    canUseExportToComputed() {
      const { role, canUseExportTo } = this;
      return canUseExportTo(role);
    },
    /**
     * Compute whether the user with a collaborator role can use the export feature based on their role.
     *
     * @returns {boolean} `true` if the user with a collaborator role can use the export feature, otherwise `false`.
     */
    canUseExportToForCollaboratorRoleComputed() {
      const { role, canUseExportToForCollaboratorRole } = this;
      return canUseExportToForCollaboratorRole(role);
    },
    email() {
      const { email = '' } = this.userInfo || {
      };
      return email;
    },
    showCustomViewsTooltip() {
      const { customViews: views = [] } = this;
      return !views.length;
    },
    isMbiUser() {
      const { email } = this;
      if (!email) return false;
      return email.includes('@mbii.com')
        || email.includes('@gosourcery.com')
        || this.usersCanUseCutSheet.includes(email);
    },
    isHlblightingUser() {
      const { email } = this;
      if (!email) return false;
      return email.includes('@hlblighting.com')
        || email.includes('@gosourcery.com')
        || this.usersCanUseCutSheet.includes(email);
    },
    isAvailableHangus() {
      const { email } = this;
      if (!email) return false;
      return email.includes('@hhangus.com')
        || email.includes('@gosourcery.com')
        || this.usersCanUseHangus.includes(email);
    },
    isElectroLightAvalilable() {
      const { email } = this;
      if (!email) return false;
      return (
        email.includes('@electrolight.com') ||
        email.includes('@gosourcery.com') ||
        this.usersCanUseElctrolight.includes(email)
      );
    },
    isAvailableMCW() {
      const { email } = this;
      if (!email) return false;
      return email.includes('@mcw.com')
        || email.includes('@gosourcery.com')
        || this.usersCanUseMCW.includes(email);
    },
    isAvailableDarkLight() {
      const { email } = this;
      if (!email) return false;
      return email.includes('@mcw.com')
        || email.includes('@darklight-design.com')
        || email.includes('@Darklight-design.com')
        || email.includes('@gosourcery')
        || this.usersCanUseDarkLight.includes(email);
    },
    isAvailableJourney() {
      const { email } = this;
      if (!email) return false;
      return email.includes('@journey.world') || email.includes('@gosourcery') || email.includes('@icrave') || this.usersCanUseJourney.includes(email);
    },
    /**
     * Get an array of items that can be used.
     *
     * @returns {Array} An array of items that can be used.
     */
    filteredExportItems() {
      /**
       * Filter the items array to include only items that can be used and have appropriate roles.
       *
       * @param {Object} item - An item object.
       * @param {boolean} [item.canUse=true] - Indicates whether the item can be used.
       *                                      Defaults to `true` if not provided.
       * @param {boolean} [item.canUseAppropriateRole=false] - Indicates whether the item can be used with appropriate roles. Defaults to `false` if not provided.
       * @returns {boolean} `true` if the item can be used and has appropriate roles, otherwise `false`.
       */
      const filterCallback = ({ canUse = true, canUseAppropriateRole = false }) =>
        canUse && canUseAppropriateRole;
      return this.exportItems.filter(filterCallback);
    },
    exportItems() {
      const {
        canUseExportToForCollaboratorRoleComputed: canUseToCollaborator,
        canUseExportToComputed: canUseExport,
      } = this;
      return [
        {
          canUse: this.isMbiUser,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            value: CUT_SHEET,
            title: MBI_LUMINAIRES_OLD,
          },
          template: TEMPLATE,
          types: [
            {
              name: DOCX,
              icon: 'mdi-file-document',
            },
            {
              name: PDF,
              icon: 'mdi-file-pdf-box',
            },
          ],
        },
        {
          canUse: this.isMbiUser,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            value: CUT_SHEET,
            title: MBI_LUMINAIRES_NEW,
          },
          template: TEMPLATE_MBI_LUMINAIRES_NEW,
          types: [
            {
              name: DOCX,
              icon: 'mdi-file-document',
            },
            {
              name: PDF,
              icon: 'mdi-file-pdf-box',
            },
          ],
        },
        {
          canUse: this.isHlblightingUser,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            value: HLB_SCHEDULE,
            title: HLB_SCHEDULE,
          },
          template: TEMPLATE_CUT_SHEET_HLB,
          types: [
            {
              name: XLSX,
              icon: 'mdi-file-excel',
            },
            {
              name: PDF,
              icon: 'mdi-file-pdf-box',
            },
          ],
        },
        {
          canUse: this.isElectroLightAvalilable,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            value: ELECTRO_VALUE,
            title: ELECTRO_LIGHT_EXPORT,
          },

          template: TEMPLATE_ELECTRO_LIGHT_LUMINAIRE_SCHEDULE,
          types: [
            {
              name: XLSX,
              icon: 'mdi-file-excel',
            },
          ],
        },
        {
          canUse: this.isAvailableHangus,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            title: HH_ANGUS_EXPORT,
            value: HH_ANGUS_EXPORT_VALUE,
          },
          template: TEMPLATE_HH_ANGUS_EXPORT,
          types: [
            {
              name: XLSX,
              icon: 'mdi-file-excel',
            },
          ],
        },
        {
          canUse: this.isAvailableMCW,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            title: MCW_EXPORT,
            value: MCW_VALUE,
          },
          template: MCW_TEMPLATE,
          types: [
            {
              name: XLSX,
              icon: 'mdi-file-excel',
            },
          ],
        },

        {
          canUse: this.isAvailableJourney,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            title: JOURNEY_WORLD,
            value: JOURNEY_WORLD,
          },
          template: '',
          types: [
            {
              name: XLSX,
              icon: 'mdi-file-excel',
            },
            {
              name: PDF,
              icon: 'mdi-file-pdf-box',
            },
          ],
        },
        {
          canUse: this.isAvailableDarkLight,
          canUseAppropriateRole: canUseToCollaborator,
          name: {
            title: DARKLIGHT_EXPORT,
            value: DARKLIGHT_VALUE,
          },
          template: DARKLIGHT_TEMPLATE,
          types: [
            {
              name: XLSX,
              icon: 'mdi-file-excel',
            },
          ],
        },
        this.currentViewExport,
        {
          name: {
            value: SPECIFICATION_SHEET,
            title: SPECIFICATION_SHEETS,
          },
          canUseAppropriateRole: canUseExport,
          template: TEMPLATE_ZIP,
          types: [
            {
              name: ZIP,
              icon: 'mdi-folder-zip',
              margin: true,
            },
          ],
        },
        {
          name: {
            value: ALL_FIELDS_CSV,
            title: 'All Fields CSV Export',
          },
          canUseAppropriateRole: canUseExport,
          template: '',
          types: [
            {
              name: CSV,
              icon: 'mdi-file-excel',
            },
          ],
        },
      ];
    },
    getWidthOfTable() {
      let tableWidth = 0;
      for (let i = 0; i < this.tableHeaders.length; i++) {
        const column = this.tableHeaders[i];
        const {
          columnSize = {
            size: 60,
            isCustomSortable: false,
          },
        } = column ?? {
        };
        const {
          size,
          isCustomSortable,
        } = columnSize;
        if (isCustomSortable) {
          tableWidth += size + SORTABLE_ACTIONS_WIDTH;
        } else {
          tableWidth += size;
        }
      }
      let listWidth;
      let result;
      if (this.printFormat === 'tabloid') {
        // Tabloid = 11×17
        listWidth = 17 * this.inc;
        result = 100 * (tableWidth / listWidth) * 1.15;
      }
      if (this.printFormat === 'a0') {
        // A0 = 33.1×46.8
        listWidth = 33.1 * this.inc;
        result = 100 * (tableWidth / listWidth);
      }
      if (this.printFormat === 'a1') {
        // A1 = 23.4×33.1
        listWidth = 23.4 * this.inc;
        result = 100 * (tableWidth / listWidth);
      }
      if (this.printFormat === 'a2') {
        // A2 = 16.5×23.4
        listWidth = 16.5 * this.inc;
        result = 100 * (tableWidth / listWidth);
      }
      return Math.ceil(result);
    },
    htmlToPdfOptions() {
      return {
        margin: 0,
        filename: `${this.getCurrentView} - ${this.getDetailProjectTitle}.pdf`,
        enableLinks: true,
        html2canvas: {
          dpi: 600,
          scale: 2,
          removeContainer: true,
        },
        jsPDF: {
          unit: 'in',
          format: this.printFormat,
          orientation: 'landscape',
          quality: 0.85,
          compress: true,
        },
      };
    },
    getScale() {
      const width = this.getWidthOfTable;
      const result = 100 / width;
      if (result > 1) {
        return 1;
      }
      return result;
    },
    isListingView() {
      return this.toggleViewCondition(this.selectedViewId);
    },
    getCurrentView() {
      const { name = '' } = this.selectedView ?? {
      };
      return name;
    },
    renderLocationText() {
      return this.exportAddress?.formatted_address || this.exportLocation;
    },
    importFromCsvSelectedName() {
      const first = this.importFromCsvFile?.[0];
      return first?.name || '';
    },
  },
  watch: {
    showExportMenu() {
      if (!this.showExportMenu) {
        this.resetSelections();
      }
    },
  },
  methods: {
    ...mapMutations({
      spinner: 'spinner',
    }),
    ...mapActions({
      handleError: 'handleError',
      declareAttachment: 'declareAttachment',
      subscribeImportProjectCSV: 'subscribeImportProjectCSV',
    }),
    changeCustomLimit({ value, type }) {
      this.customLimitsByView = {
        ...this.customLimitsByView,
        [type]: value,
      };
    },
    async downloadCsvTemplate() {
      this.spinner(true);
      try {
        const { data } = await API.getTemplateUrl({
          filename: rawConstants.IMPORT_PROJECT_CSV_TEMPLATE,
        });
        const link = data.response;
        window.open(link, '_blank');
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    setAllCaps(val) {
      this.allCaps = val;
    },
    ...mapMutations({
      openSnackBar: 'openSnackBar',
    }),
    selectItem(val, name) {
      this.$set(this, name, val);
    },
    hasDownloaded() {
      this.isVisibleTable = false;
    },
    onProgress(event) {
      const isOver = event === 100;

      if (isOver) {
        this.spinner(false);
      }
    },
    // Export Dialog
    exportItemClicked(exportParams) {
      if (exportParams.name === CURRENT_VIEW_VALUE) {
        this.openExportDialog(exportParams);
      } else {
        this.performExport(exportParams);
      }
    },
    async openExportDialog(exportParams) {
      this.exportParams = exportParams;
      await this.initExportDialogFields();
      this.exportDialog = true;
    },
    async importFromCsv() {
      const { file, type, name: filename } = this.importFromCsvFile[0];
      const {
        scheduleId,
        projectDetails,
      } = this;
      const projectId = projectDetails?.id || this.$route.params.id;
      const missingRequiredMetadata = [
        projectId,
        scheduleId,
      ].some(val => !val);
      if (missingRequiredMetadata) {
        this.handleError('Unable to import CSV: required project metadata is missing.');
        return;
      }
      this.spinner(true);
      try {
        await this.subscribeImportProjectCSV({
          projectId,
        });
        await this.declareAttachment({
          file: {
            file,
            type,
          },
          initialMetadata: {
            operation: 'project_import',
            project_id: projectId,
            schedule_id: scheduleId,
          },
          filename,
        });
        this.openSnackBar({
          text: 'Products CSV has been successfully uploaded. The products will appear in the project after a while.',
        });
        this.importFromCsvDialog = false;
        this.importFromCsvFile = [];
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    async initExportDialogFields() {
      this.exportProjectName = this.projectDetails.projectName;
      this.exportProjectNumber = this.projectDetails.number;
      this.exportAuthor = this.activeWorkspaceName;
      this.exportAddress = this.projectDetails.address;
      this.exportLocation = this.projectDetails.location;
      this.exportWSNotes = this.workspaceSpecNotes;
      // This is NOT Project Notes, but the SpecNotes. Confusing, I know.
      this.exportProjectNotes = this.projectSpecNotes;

      if (this.isVersionMode) {
        this.exportIssuanceDate = this.$formatProjectDates(this.getCurrentVersionTimestamp, true);
        this.exportPhase = this.versionTitle;
      } else {
        const now = new Date().toISOString();
        this.exportIssuanceDate = this.$formatProjectDates(now, true);
        this.exportPhase = this.projectDetails.phase;
      }
    },
    clearImportFromCsvFile() {
      this.importFromCsvFile = [];
    },
    inputFilter(newFile, oldFile, prevent) {
      let val;
      if (newFile && !oldFile) {
        if (newFile.file && !newFile.file.type.includes('csv')) {
          this.handleError('Only CSV format is allowed to be uploaded');
          val = prevent();
        }
      } return val;
    },
    performExportWithMeta() {
      const params = {
        ...this.exportParams,
        exportMeta: {
          name: this.exportProjectName,
          issuanceDate: this.exportIssuanceDate,
          number: this.exportProjectNumber,
          author: this.exportAuthor,
          location: this.renderLocationText,
          phase: this.exportPhase,
          wsNotes: this.exportWSNotes ?? '',
          projectNotes: this.exportProjectNotes ?? '',
        },
      };
      this.performExport(params);
    },
    performExport(params) {
      this.exporting = true;
      try {
        this.exportProjectHandle(params);
        this.exportDialog = false;
      } finally {
        this.exporting = false;
      }
    },
    useAiCsvCreator() {
      window.open(AI_CSV_CREATOR, '_blank');
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep .v-list-item__icon {
  align-self: center;
}

::v-deep.v-list-group > .v-list-group__items > .v-list-item__subtitle {
  padding-inline-start: 16px;
}

.type-item-custom-icon {
  width: 24px;
  padding: 1px;
  margin-right: 1px;
  filter: grayscale(1);
  opacity: .9;
}

.export-notes {
  ::v-deep .v-input__slot {
    padding-right: 0.25rem !important;
  }
  ::v-deep textarea {
    overflow-y: auto !important;
    padding-right: 0.25rem !important;
  }
}

.import-csv-filename-row {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.import-csv-filename__text {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>